import React, { useRef, useState } from "react";
import "./CheckPP.css";
import API from "../api/api";
import { Snackbar, Alert } from "@mui/material";

function CheckPP({
    setAnalysisData,
    setAnalyzeLoading,
    setModalOpen,
    setLoading,
    loading,
    compareLoading,
    resetBeforeAnalyze,
}) {
    const [pdfInputs, setPdfInputs] = useState([
        { id: Date.now(), files: [] }
    ]);
    const [excelFile, setExcelFile] = useState(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");

    const pdfInputRefs = useRef({});
    const excelInputRef = useRef(null);

    const getPdfCount = (files) => {
        if (!files || files.length === 0) return "Файлов: 0";
        return `Файлов: ${files.length}`;
    };

    const getExcelCount = (file) => {
        return file ? "Файлов: 1" : "Файлов: 0";
    };

    const getPdfNamesPreview = (files) => {
        if (!files || files.length === 0) return "Файлы не выбраны";
        if (files.length === 1) return files[0].name;
        if (files.length === 2) return `${files[0].name}, ${files[1].name}`;
        return `${files[0].name}, ${files[1].name} +${files.length - 2}`;
    };

    const showSnackbar = (msg, severity = "info") => {
        setSnackbarMessage(msg);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleFilesChange = (index, files) => {
        const updated = [...pdfInputs];
        updated[index].files = Array.from(files);
        setPdfInputs(updated);
    };

    const handleExcelChange = (file) => {
        setExcelFile(file || null);
    };

    const addPdfInput = () => {
        setPdfInputs((prev) => [
            ...prev,
            { id: Date.now() + Math.random(), files: [] }
        ]);
    };

    const removePdfInput = (id) => {
        if (pdfInputs.length === 1) return;

        const inputEl = pdfInputRefs.current[id];
        if (inputEl) {
            inputEl.value = "";
        }

        setPdfInputs((prev) => prev.filter((input) => input.id !== id));
        delete pdfInputRefs.current[id];
    };

    const clearPdfInput = (index, id) => {
        const updated = [...pdfInputs];
        updated[index].files = [];
        setPdfInputs(updated);

        const inputEl = pdfInputRefs.current[id];
        if (inputEl) {
            inputEl.value = "";
        }
    };

    const clearExcel = () => {
        setExcelFile(null);

        if (excelInputRef.current) {
            excelInputRef.current.value = "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loading) {
            return showSnackbar("Предварительный анализ уже выполняется", "warning");
        }

        if (compareLoading) {
            return showSnackbar("Идет сравнение, подождите...", "warning");
        }

        const allFiles = pdfInputs.flatMap((input) => input.files);

        if (!allFiles.length) {
            return showSnackbar("Добавьте PDF файлы", "error");
        }

        if (!excelFile) {
            return showSnackbar("Добавьте Excel файл", "error");
        }

        const MAX_TOTAL_FILES = 200;
        const totalFilesCount = allFiles.length + 1;

        if (totalFilesCount > MAX_TOTAL_FILES) {
            return showSnackbar(
                `Превышено допустимое количество файлов: максимум ${MAX_TOTAL_FILES}, выбрано ${totalFilesCount}`,
                "error"
            );
        }

        const formData = new FormData();
        allFiles.forEach((file) => formData.append("pdf_files", file));
        formData.append("excel_file", excelFile);

        resetBeforeAnalyze();

        setLoading(true);
        setAnalyzeLoading(true);
        setModalOpen(true);

        try {
            const response = await API.post("/checker/analyze-pp/", formData);
            const data = response.data;

            const filesMap = {};
            allFiles.forEach((file) => {
                filesMap[file.name] = file;
            });

            const files_ready = (data.files_ready || []).map((fileInfo) => ({
                ...fileInfo,
                fileObj: filesMap[fileInfo.file],
            }));

            const finalData = {
                ...data,
                files: allFiles,
                files_ready,
                excelFile,
            };

            setAnalysisData(finalData);
            showSnackbar("Анализ завершён", "success");
        } catch (error) {
            console.error(error);
            setModalOpen(false);

            let message = "Ошибка анализа. Проверьте количество и размер файлов";

            if (error.response) {
                if (error.response.status === 401) {
                    message = "Необходимо авторизоваться для отправки файлов";
                } else if (error.response.status === 400 && error.response.data?.error) {
                    message = error.response.data.error;
                } else if (error.response.status === 413) {
                    message = "Слишком большой размер файлов";
                } else {
                    message = `Ошибка при обработке файлов (${error.response.status}). Проверьте количество и размер файлов`;
                }
            } else if (error.request) {
                message = "Нет соединения с сервером";
            }

            showSnackbar(message, "error");
        } finally {
            setAnalyzeLoading(false);
            setLoading(false);
        }
    };

    return (
        <section className="checkpp-shell">
            <div className="checkpp-card">

                <div className="checkpp-topbar">
                    <div className="checkpp-badge">Проверка платежных поручений</div>
                    <h2 className="checkpp-title">Сравнение банка и учета</h2>
                </div>                

                <div className="checkpp-body">
                    <form onSubmit={handleSubmit}>
                        <div className="checkpp-grid">
                            <div className="checkpp-panel">
                                <div className="panel-header">
                                    <div>
                                        <div className="panel-kicker">Источник 1</div>
                                        <div className="panel-title">PDF банка</div>
                                    </div>

                                    <button
                                        type="button"
                                        className="panel-add-btn"
                                        onClick={addPdfInput}
                                    >
                                        + еще
                                    </button>
                                </div>

                                <div className="upload-list">
                                    {pdfInputs.map((input, index) => (
                                        <div key={input.id} className="upload-item">
                                            <div className="upload-item-head">
                                                <div className="upload-item-labels">
                                                    <span className="upload-item-title">
                                                        Блок {index + 1}
                                                    </span>
                                                    <span className="upload-item-count">
                                                        {getPdfCount(input.files)}
                                                    </span>
                                                </div>

                                                <div className="upload-item-actions">
                                                    {input.files?.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="btn-secondary"
                                                            onClick={() => clearPdfInput(index, input.id)}
                                                        >
                                                            очистить
                                                        </button>
                                                    )}

                                                    {pdfInputs.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn-remove"
                                                            onClick={() => removePdfInput(input.id)}
                                                        >
                                                            удалить
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="upload-row">
                                                <label className="file-btn">
                                                    Выбрать PDF
                                                    <input
                                                        ref={(el) => {
                                                            if (el) {
                                                                pdfInputRefs.current[input.id] = el;
                                                            }
                                                        }}
                                                        type="file"
                                                        accept=".pdf"
                                                        multiple
                                                        onChange={(e) =>
                                                            handleFilesChange(index, e.target.files)
                                                        }
                                                        hidden
                                                    />
                                                </label>

                                                <div className="file-preview">
                                                    {getPdfNamesPreview(input.files)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="checkpp-panel">
                                <div className="panel-header solo">
                                    <div>
                                        <div className="panel-kicker">Источник 2</div>
                                        <div className="panel-title">Excel учета</div>
                                    </div>
                                </div>

                                <div className="upload-item excel-item">
                                    <div className="upload-item-head">
                                        <div className="upload-item-labels">
                                            <span className="upload-item-title">Файл учета</span>
                                            <span className="upload-item-count">
                                                {getExcelCount(excelFile)}
                                            </span>
                                        </div>

                                        <div className="upload-item-actions">
                                            {excelFile && (
                                                <button
                                                    type="button"
                                                    className="btn-remove"
                                                    onClick={clearExcel}
                                                >
                                                    удалить
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="upload-row">
                                        <label className="file-btn excel">
                                            Выбрать Excel
                                            <input
                                                ref={excelInputRef}
                                                type="file"
                                                accept=".xlsx,.xls"
                                                onChange={(e) =>
                                                    handleExcelChange(e.target.files[0])
                                                }
                                                hidden
                                            />
                                        </label>

                                        <div className="file-preview excel-preview">
                                            {excelFile ? excelFile.name : "Файл не выбран"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={loading || compareLoading}
                            >
                                {loading
                                    ? "Идет предварительный анализ..."
                                    : compareLoading
                                        ? "Идет сравнение..."
                                        : "Предварительный анализ"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ top: "72px" }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </section>
    );
}

export default CheckPP;